
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Lock } from "lucide-react";

interface UpgradePromptProps {
  feature: string;
  description: string;
  height?: number;
  requiredPlan?: 'basic' | 'premium';
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({ 
  feature, 
  description, 
  height = 120,
  requiredPlan = 'basic'
}) => {
  // Set styling based on required plan
  const isPremium = requiredPlan === 'premium';
  const bgColor = isPremium ? 'bg-sauna-orange/10' : 'bg-sauna-blue/10';
  const textColor = isPremium ? 'text-sauna-orange' : 'text-sauna-blue';
  const buttonGradient = isPremium 
    ? 'bg-gradient-to-r from-sauna-orange to-sauna-orange/90 hover:from-sauna-orange/90 hover:to-sauna-orange' 
    : 'bg-gradient-to-r from-sauna-blue to-sauna-blue/90 hover:from-sauna-blue/90 hover:to-sauna-blue';
  
  return (
    <Card className="relative overflow-hidden shadow-sm border border-gray-100 transition-all hover:shadow-md">
      <CardContent className="p-0">
        <div className="flex items-center justify-center h-full">
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/95 backdrop-blur-[2px] z-10 p-6">
            <div className={`${bgColor} rounded-full p-2 mb-3`}>
              <Lock className={`h-5 w-5 ${textColor}`} />
            </div>
            <h3 className="text-lg font-medium mb-1.5 text-sauna-navy">{feature}</h3>
            <p className="text-sm text-center text-muted-foreground mb-4 max-w-xs">{description}</p>
            <Button className={`${buttonGradient} shadow-sm hover:shadow text-white border-none`}>
              Upgrade to {isPremium ? 'Premium' : 'Basic'}
            </Button>
          </div>
          
          <div className="w-full space-y-3 p-6" style={{ height: `${height}px` }}>
            {feature === "Profile Views" && (
              <>
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-8 w-1/4" />
                <Skeleton className="h-4 w-1/3" />
              </>
            )}
            
            {feature === "Local Rankings" && (
              <>
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-8 w-1/5" />
                <Skeleton className="h-4 w-1/3" />
              </>
            )}
            
            {feature === "Customer Messages" && (
              <>
                <Skeleton className="h-4 w-1/2" />
                <div className="flex items-center gap-2">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <Skeleton className="h-5 w-3/4" />
                </div>
              </>
            )}
            
            {!["Profile Views", "Local Rankings", "Customer Messages"].includes(feature) && (
              <>
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-4/6" />
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default UpgradePrompt;
